import React, { useState, useRef, useEffect, Suspense, lazy } from "react";
import Navbar from "./Navbar";
import { useMsal } from "@azure/msal-react";
import { useNavigate } from "react-router-dom";

import UsersAttendance from "./UsersAttendance";
import EmployeeData from "./EmployeeData";
import UsersList from "./UsersList";
import UsersInventory from "./UsersInventory";

import QuotationsTable from "./Quotations";
import PurchaseOrdersTable from "./PurchaseOrdersTable";
import InvoicesTable from "./InvoicesTable";

import UserFeatureRequest from "./UserFeatureRequest";
import UserDocumentStorage from "./UserDocumentStorage";

import CustomerSupplierData from "./CustomerSupplierData";
import CustomerSupplierMap from "./CustomerSupplierMap";

import CostAccounting from "./CostAndAccounts";

import UserMistakes from "./UserMistakes";
import AlertsAndTasksTable from "./AlertsAndTasks"
import ERPDashboardInfo from "./ERPDashboardInfo"

import { useDashboard } from './dashboardStore';

import arrowupLogo from "/arrowup.png"

import ChangesAndRecommendations from "./ChangesAndRecommendations";
const StatsCostSection = React.lazy(() =>
  import("./StatsCostSection")
);


const customLinks = [
  { href: "/", label: "Home" },
  { href: "/#about", label: "About Us" },
  { href: "/#services", label: "Our Service" },
  { href: "/#newsletter", label: "Contact Us" },
];
const apiBaseUrl = import.meta.env.VITE_API_BASE_URL;

const Dashboard = () => {

  const [showTopBtn, setShowTopBtn] = useState(false);
  useEffect(() => {
    // Only run once on dashboard mount
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 300) {
        setShowTopBtn(true);
      } else {
        setShowTopBtn(false);
      }
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const { instance } = useMsal();
  const navigate = useNavigate();
  const [role, setRole] = useState("loading");

  const [isLoggedIn, setIsLoggedIn] = useState(true);
  const [userDisplayName, setUserDisplayName] = useState(
    instance.getActiveAccount()?.name || "UNKNOWN USER"
  );

  const allUsersRef = useRef(null);
  const attendanceRef = useRef(null);
  const inventoryRef = useRef(null);
  const quotationsRef = useRef(null);
  const purchaseOrdersRef = useRef(null);
  const invoicesRef = useRef(null);
  const customerRef = useRef(null);
  const featureReqRef = useRef(null);
  const documentStorageRef = useRef(null);

  const customerSupplierRef = useRef(null);
  const customerSupplierMapRef = useRef(null);

  const costAndAccountRef = useRef(null);

  const employeeDataRef = useRef(null);
  const mistakesAndAIRef = useRef(null);
  const alertsAndTasksRef = useRef(null);

  const changesAndRecommendationsRef = useRef(null);
  const statsAndCostsRef = useRef(null);
  const erpInfoRef = useRef(null);
  // Inside Dashboard component, above return

  const signOutClickHandler = () => {
    if (!instance.getActiveAccount()) return
    const logoutRequest = {
      account: instance.getActiveAccount(),
      postLogoutRedirectUri: "/", // redirect after logout
    }
    instance.logoutRedirect(logoutRequest)
  }


  const handleLogout = async () => {
    setIsLoggedIn(false);
    setUserDisplayName("LOGGED OUT");
    instance.setActiveAccount(null);
    navigate("/");
  };


  const scrollToSection = (ref) => {
    ref.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    const checkRole = async () => {
      const account = instance.getActiveAccount();
      if (!account) return; // just in case

      try {
        const request = {
          scopes: ["User.Read.All"],
          account,
        };

        // make sure MSAL is ready
        if (!instance) return;

        const tokenResponse = await instance.acquireTokenSilent(request);
        const accessToken = tokenResponse.accessToken;

        const res = await fetch(`${apiBaseUrl}/api/is-admin`, {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        });

        if (!res.ok) throw new Error(await res.text());
        const data = await res.json();
        setRole(data.role); //TODO
      } catch (err) {
        console.error("Error fetching role:", err.message);
        setRole("error");
      }
    };

    // defer until next tick to let MSAL initialize
    setTimeout(() => checkRole(), 0);
  }, [instance]);


  // Load from context

  const load = useDashboard(state => state.load);
  const didLoad = useRef(false);

  useEffect(() => {
    if (didLoad.current) return;  // already loaded, skip
    didLoad.current = true;
    load();                       // trigger the fetch
  }, []);




  const super_admin_sections = [
    "Changes & Recommendations",
    "Alerts & Tasks",
    "Attendance",
    "Employee Data",
    "Purchase Orders",

    "Quotations",
    "Invoices",
    "Inventory",
    "Costs & Accounting",

    "Customer Supplier Data",
    "Customer Supplier Map",

    "Stats & Costs",
    "ERP Info",
    "Mistakes & AI",
    "Document Storage",

    "All Users",
    "Customer Track",
    "Feature Request"
  ];


  const to_remove_admin = ["Inventory"]; // sections you want to remove for admins
  const admin_sections = super_admin_sections.filter(
    section => !to_remove_admin.includes(section)
  );

  const to_remove_staff = ["All Users", "Employee Data"]
  const staff_sections = admin_sections.filter(
    section => !to_remove_staff.includes(section)
  );

  let all_sections = staff_sections;
  if (role === "Super Admin") {
    all_sections = super_admin_sections;
  } else if (role === "Admin") {
    all_sections = admin_sections;
  }


  const [search, setSearch] = useState("");
  const filteredSections = all_sections.filter((sec) =>
    sec.toLowerCase().includes(search.toLowerCase())
  );

  const sectionComponents = {
    "Changes & Recommendations": <ChangesAndRecommendations />,
    "Alerts & Tasks": <AlertsAndTasksTable />,
    "Attendance": <UsersAttendance />,
    "Employee Data": <EmployeeData />,
    "All Users": <UsersList />,

    "Inventory": <UsersInventory />,
    "Purchase Orders": <PurchaseOrdersTable parentRef={purchaseOrdersRef} />,
    "Quotations": <QuotationsTable parentRef={quotationsRef} />,
    "Invoices": <InvoicesTable />,

    "Customer Track": (
      <button
        className="w-1/2 bg-blue-600 text-white px-8 py-4 rounded-xl hover:bg-blue-700 cursor-pointer 
                 transition-all hover:shadow-lg hover:shadow-blue-100 active:scale-95"
        onClick={() => navigate("/customer")}
      >
        Go To Customer Section →
      </button>
    ),
    "Feature Request": <UserFeatureRequest />,
    "Document Storage": <UserDocumentStorage />,

    "Customer Supplier Data": <CustomerSupplierData />,
    "Customer Supplier Map": <CustomerSupplierMap />,
    "Stats & Costs": (
      <Suspense fallback={null}>
        <StatsCostSection />
      </Suspense>
    ),
    "ERP Info": <ERPDashboardInfo />,
    "Mistakes & AI": <UserMistakes />,
    "Costs & Accounting": <CostAccounting />,

  };

  return (
    <div className="min-h-screen flex flex-col">

      <Navbar links={customLinks} />

      <main className="max-w-[1440px] w-full mx-auto px-4 md:px-20 flex flex-col gap-8 py-8 ">
        <div className="flex flex-col items-center justify-center text-center bg-gray-50 rounded-lg shadow-md mt-20 pb-2">
          {isLoggedIn ? (
            <>
              <h1 className="text-3xl sm:text-4xl font-bold text-gray-800">Dashboard</h1>
              <p className="text-lg mt-2">
                Welcome, {userDisplayName} <br />
                Role:{" "}
                <span
                  className={
                    role === "Super Admin"
                      ? "text-blue-700 font-semibold"
                      : role === "Admin"
                        ? "text-blue-600 font-semibold"
                        : "text-gray-600"
                  }
                >
                  {role}
                </span>
              </p>
              <button
                className="mt-4 px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition w-full sm:w-auto cursor-pointer"
                onClick={signOutClickHandler}
              >
                Logout

              </button>

              <div className="max-w-[800px] mx-auto mt-8">
                {/* Search Input */}
                <input
                  type="text"
                  placeholder="Search sections..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className={`w-full p-4 mb-4 border rounded-lg shadow-sm focus:outline-none focus:ring focus:border-blue-400 ${search ? "bg-blue-200" : "bg-white"
                    }`}
                />

                {/* Grid */}
                <div className="hidden md:grid grid-cols-4 gap-4 text-gray-700 text-lg font-base h-[500px] overflow-auto p-2">
                  {filteredSections.map((sec, idx) => (
                    <span
                      key={sec}
                      className="hover:text-blue-600 hover:bg-blue-200 cursor-pointer flex flex-col justify-center items-center min-w-[150px]
        h-[80px] px-3 py-4 border rounded-lg shadow-sm bg-white transition"
                      onClick={() =>
                        scrollToSection({
                          "Changes & Recommendations": changesAndRecommendationsRef,
                          "Alerts & Tasks": alertsAndTasksRef,
                          Attendance: attendanceRef,
                          "Employee Data": employeeDataRef,
                          "All Users": allUsersRef,

                          Inventory: inventoryRef,
                          "Purchase Orders": purchaseOrdersRef,
                          "Quotations": quotationsRef,
                          Invoices: invoicesRef,

                          "Customer Track": customerRef,
                          "Feature Request": featureReqRef,
                          "Document Storage": documentStorageRef,

                          "Customer Supplier Data": customerSupplierRef,
                          "Customer Supplier Map": customerSupplierMapRef,
                          "Stats & Costs": statsAndCostsRef,
                          "ERP Info": erpInfoRef,
                          "Mistakes & AI": mistakesAndAIRef,
                          "Costs & Accounting": costAndAccountRef,
                        }[sec])
                      }
                    >
                      {idx + 1}. {sec}
                      <div className="flex flex-row">
                        {role === "Super Admin" &&
                          (to_remove_admin.includes(sec) || to_remove_staff.includes(sec)) && (
                            <span className="ml-2 px-2 py-0.5 rounded-full text-xs font-semibold text-white bg-blue-800 hidden lg:inline">
                              SA
                            </span>
                          )}
                        {(role === "Super Admin" || role === "Admin") &&
                          to_remove_staff.includes(sec) && (
                            <span className="ml-2 px-2 py-0.5 rounded-full text-xs font-semibold text-white bg-blue-400 hidden lg:inline">
                              A
                            </span>
                          )}
                      </div>
                    </span>
                  ))}
                  <span
                    className="hover:text-red-600 hover:bg-red-200 cursor-pointer flex flex-col text-red-500
    justify-center items-center h-[80px] px-3 py-4 border rounded-lg shadow-sm bg-white transition"
                    onClick={() => {
                      setSearch("");
                      setTimeout(() => scrollToTop(), 0);
                    }}
                  > Clear Search </span>
                </div>
              </div>



            </>
          ) : (
            <a
              href="/login"
              className="mt-4 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition w-full sm:w-auto cursor-pointer"
            >
              Log In
            </a>
          )}
        </div>

        {/* Sections */}
        {all_sections.filter((sec) => filteredSections.includes(sec)).map((sec, idx) => {
          const refMap = {
            "Changes & Recommendations": changesAndRecommendationsRef,

            "Alerts & Tasks": alertsAndTasksRef,
            "Attendance": attendanceRef,
            "Employee Data": employeeDataRef,
            "All Users": allUsersRef,

            "Inventory": inventoryRef,
            "Purchase Orders": purchaseOrdersRef,
            "Quotations": quotationsRef,
            "Invoices": invoicesRef,

            "Customer Track": customerRef,
            "Feature Request": featureReqRef,
            "Document Storage": documentStorageRef,

            "Customer Supplier Data": customerSupplierRef,
            "Customer Supplier Map": customerSupplierMapRef,
            "Stats & Costs": statsAndCostsRef,
            "ERP Info": erpInfoRef,

            "Mistakes & AI": mistakesAndAIRef,
            "Costs & Accounting": costAndAccountRef,
          };
          return (
            <div
              key={sec}
              ref={refMap[sec]}
              className="flex flex-col items-start justify-center text-center mt-20"
            >
              <h1 className="text-4xl font-semibold text-gray-800 mb-2">
                {idx + 1}. {sec == "Document Storage" ? "Document Storage (Secure General Storage)" : sec}

                {role === "Super Admin" && (to_remove_admin.includes(sec) || to_remove_staff.includes(sec)) && (
                  <span className="ml-2 px-2 py-0.5 rounded-full text-xs font-semibold text-white bg-blue-800">
                    Super Admin
                  </span>
                )}
                {(role === "Super Admin" || role === "Admin") && to_remove_staff.includes(sec) && (
                  <span className="ml-2 px-2 py-0.5 rounded-full text-xs font-semibold text-white bg-blue-400">
                    Admin
                  </span>
                )}


              </h1>
              <div className="flex flex-1 flex-col items-center w-full">
                {sectionComponents[sec]}
              </div>
            </div>
          );
        })}

      </main>


      {/* Scroll to Top Button */}
      {showTopBtn && (
        <>
          {/* Search Input & Clear Button */}
          <div className="flex flex-col md:flex-col items-center md:items-end gap-2 fixed bottom-4 right-4 md:bottom-10 md:right-5 z-50 w-[90%] max-w-xs md:w-auto">
            <input
              type="text"
              placeholder="Search sections..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className={`w-full p-3 md:p-4 border rounded-lg shadow-sm focus:outline-none focus:ring focus:border-blue-400 ${search ? "bg-blue-200" : "bg-white"}`}
            />
            <span
              className="w-full md:w-auto text-center hover:text-red-600 hover:bg-red-200 cursor-pointer flex justify-center items-center px-4 py-2 border rounded-lg shadow-sm text-red-500 transition"
              onClick={() => {
                setSearch("");
                setTimeout(() => scrollToTop(), 0);
              }}
            >
              Clear Search
            </span>
          </div>

          {/* Scroll To Top Button */}
          <button
            onClick={scrollToTop}
            className="fixed cursor-pointer bottom-40 right-4 md:right-10 p-6 md:p-6 bg-blue-400 text-white rounded-full shadow-lg hover:bg-blue-500 transition z-50"
            title="Go to top"
          >
            <img src={arrowupLogo} alt="Go up" className="w-6 h-6 filter brightness-0 invert" />
          </button>
        </>
      )}

    </div>
  );
};

export default Dashboard;
